// 아이템 베이스 클래스
class Item {
  constructor(scene, x = 0, z = 0) {
    this.scene = scene;
    this.model = null;
    this.mixer = null;
    this.position = new THREE.Vector3(x, 0, z);
    this.isLoaded = false;
    this.isCollected = false;

    // 물리적 특성
    this.collectionRadius = 1.0; // 수집 가능한 범위
    this.rotationSpeed = 2.0; // 회전 속도
    this.hoverHeight = 1.0; // 바닥에서 떠있는 높이
    this.hoverSpeed = 1.0; // 위아래 움직임 속도
    this.hoverRange = 0.1; // 위아래 움직임 범위
    this.startTime = performance.now(); // 생성 시간

    // 아이템별 설정 (하위 클래스에서 오버라이드)
    this.modelPath = "";
    this.effectDescription = "";
    this.scale = 1;
  }

  // 아이템 초기화 (모델 로드)
  async init() {
    try {
      if (!this.modelPath) {
        throw new Error("모델 경로가 설정되지 않았습니다.");
      }

      const loader = new THREE.GLTFLoader();
      const gltf = await new Promise((resolve, reject) => {
        loader.load(
          this.modelPath,
          (gltf) => resolve(gltf),
          undefined,
          (error) => reject(error)
        );
      });

      this.model = gltf.scene;
      this.model.position.copy(this.position);
      this.model.position.y = this.hoverHeight;

      this.model.traverse((child) => {
        if (child.isMesh) {
          // 그림자는 받지만 생성하지 않음
          child.castShadow = true;
          child.receiveShadow = false;
        }
      });

      // 스케일 조정 (필요에 따라)
      this.model.scale.setScalar(this.scale);

      // 애니메이션 믹서 설정 (필요한 경우)
      if (gltf.animations && gltf.animations.length > 0) {
        this.mixer = new THREE.AnimationMixer(this.model);
        // 기본 애니메이션 재생 (있는 경우)
        const action = this.mixer.clipAction(gltf.animations[0]);
        action.play();
      }

      this.scene.add(this.model);
      this.isLoaded = true;

      // Utils.log(
      //   `${this.constructor.name} 아이템이 (${this.position.x}, ${this.position.z}) 위치에 생성되었습니다.`
      // );
    } catch (error) {
      Utils.log(
        `${this.constructor.name} 아이템 로드 실패: ${error.message}`,
        "error"
      );
      throw error;
    }
  }

  // 매 프레임 업데이트
  update(deltaTime) {
    if (!this.isLoaded || this.isCollected) return;

    // 회전 애니메이션
    if (this.model) {
      this.model.rotation.y += this.rotationSpeed * deltaTime;

      // 위아래 부유 애니메이션
      const elapsedTime = (performance.now() - this.startTime) / 1000;
      const hoverOffset =
        Math.sin(elapsedTime * this.hoverSpeed) * this.hoverRange;
      this.model.position.y = this.hoverHeight + hoverOffset;
    }

    // 애니메이션 믹서 업데이트
    if (this.mixer) {
      this.mixer.update(deltaTime);
    }
  }

  // 플레이어와의 충돌 검사
  checkCollision(playerPosition) {
    if (!this.isLoaded || this.isCollected) return false;

    const distance = Utils.distance(this.position, playerPosition);
    return distance <= this.collectionRadius;
  }

  // 아이템 수집 처리
  collect(player) {
    if (this.isCollected) return false;

    this.isCollected = true;

    // 플레이어 정보를 저장해두고 애니메이션 끝에 효과 적용
    this.collectedPlayer = player;

    // 멋진 수집 애니메이션 실행 (플레이어 참조를 전달하여 실시간 위치 추적)
    this.playAdvancedCollectionEffect(player);

    // Utils.log(`${this.constructor.name} 아이템이 수집되었습니다!`);
    return true;
  }

  // 아이템 효과 적용 (하위 클래스에서 오버라이드)
  applyEffect(player) {
    // 기본 구현: 아무것도 하지 않음
    // Utils.log("기본 아이템 효과가 적용되었습니다.");
  }

  // 고급 수집 애니메이션 (밀려났다가 빨려들어오는 효과)
  playAdvancedCollectionEffect(player) {
    if (!this.model) return;

    const startPosition = this.model.position.clone();
    const startScale = this.model.scale.x;

    // 총 애니메이션 지속 시간
    const totalDuration = 0.8; // 0.8초
    const pushPhase = 0.3; // 밀려나는 단계 (0.3초)
    const pullPhase = 0.5; // 빨려들어오는 단계 (0.5초)

    const startTime = Date.now();

    const animate = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const progress = Math.min(elapsed / totalDuration, 1.0);

      if (progress < 1.0) {
        // 실시간으로 플레이어 위치 가져오기
        const currentPlayerPosition = player.position.clone();

        if (elapsed < pushPhase) {
          // 1단계: 밀려나는 효과
          const pushProgress = elapsed / pushPhase;
          const easeOut = 1 - Math.pow(1 - pushProgress, 3); // easeOut 곡선

          // 플레이어 반대 방향으로 밀려나는 벡터 계산 (실시간)
          const pushDirection = new THREE.Vector3()
            .subVectors(startPosition, currentPlayerPosition)
            .normalize();

          // 밀려나는 거리 설정
          const pushDistance = 2.0;
          const pushTarget = startPosition
            .clone()
            .add(pushDirection.multiplyScalar(pushDistance));

          // 위치 변경 (밀려나기)
          const currentPos = startPosition.clone().lerp(pushTarget, easeOut);
          this.model.position.copy(currentPos);

          // 크기 약간 증가
          const scaleMultiplier = 1 + 0.5 * easeOut;
          this.model.scale.setScalar(startScale * scaleMultiplier);
        } else {
          // 2단계: 빨려들어오는 효과 (자석처럼)
          const pullProgress = (elapsed - pushPhase) / pullPhase;
          const easeIn = Math.pow(pullProgress, 3); // 더 강한 easeIn 곡선으로 급가속 효과

          // 플레이어 반대 방향으로 밀려나는 벡터 계산 (실시간)
          const pushDirection = new THREE.Vector3()
            .subVectors(startPosition, currentPlayerPosition)
            .normalize();

          // 밀려나는 거리 설정
          const pushDistance = 2.0;
          const pushTarget = startPosition
            .clone()
            .add(pushDirection.multiplyScalar(pushDistance));

          // 플레이어 위치로 빨려들어가기 (곡선 경로로, 실시간 위치 사용)
          const currentPos = pushTarget
            .clone()
            .lerp(currentPlayerPosition, easeIn);

          // 자연스러운 곡선 궤적을 위한 사인파 추가
          const waveOffset =
            Math.sin(pullProgress * Math.PI * 2) * 0.5 * (1 - pullProgress);
          const perpendicular = new THREE.Vector3()
            .subVectors(currentPlayerPosition, pushTarget)
            .cross(new THREE.Vector3(0, 1, 0))
            .normalize()
            .multiplyScalar(waveOffset);

          currentPos.add(perpendicular);
          this.model.position.copy(currentPos);

          // 크기 줄어들면서 회전 가속 (더 드라마틱하게)
          const scaleMultiplier = 1.3 - 1.2 * easeIn;
          this.model.scale.setScalar(
            startScale * Math.max(0.05, scaleMultiplier)
          );

          // 회전 속도 급격히 증가 (빨려들어가는 느낌)
          this.model.rotation.y +=
            this.rotationSpeed *
            8 *
            pullProgress *
            pullProgress *
            (elapsed - pushPhase);
          this.model.rotation.x +=
            this.rotationSpeed * 3 * pullProgress * (elapsed - pushPhase);

          // 투명도 감소 (마지막 0.3초 동안 더 길게)
          const fadeProgress = Math.max(0, (pullProgress - 0.5) / 0.5);
          const opacity = 1.0 - fadeProgress * fadeProgress; // 부드러운 페이드
          this.model.traverse((child) => {
            if (child.isMesh && child.material) {
              child.material.opacity = opacity;

              // 발광 효과 강화 (아이템별로 다른 색상)
              this.applyCollectionGlow(child, pullProgress);
            }
          });
        }

        requestAnimationFrame(animate);
      } else {
        // 개별 아이템 수집 효과 실행 (포인트 증가 포함)
        this.playCollectionEffect();
        this.destroy();
      }
    };

    animate();
  }

  // 아이템별 발광 효과 (하위 클래스에서 오버라이드 가능)
  applyCollectionGlow(child, progress) {
    // 기본 발광 효과 (연한 흰색)
    const glowIntensity = 0x333333 + Math.floor(0x222222 * progress);
    child.material.emissive.setHex(glowIntensity);
  }

  // 아이템별 고유 수집 효과 (하위 클래스에서 오버라이드)
  playCollectionEffect() {
    // 기본 구현: 로그만 출력
    // Utils.log(`✨ ${this.constructor.name} 수집 효과!`);
  }

  // 아이템 제거
  destroy() {
    if (this.model) {
      this.scene.remove(this.model);
    }
    this.isCollected = true;
  }

  // 로드 상태 확인
  getLoadStatus() {
    return this.isLoaded;
  }

  // 수집 상태 확인
  getCollectedStatus() {
    return this.isCollected;
  }

  // 위치 가져오기
  getPosition() {
    return this.position.clone();
  }
}

// webpack 빌드를 위해 default export 추가
export default Item;

// 전역 변수로도 등록 (기존 코드 호환성)
window.Item = Item;
